import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteContractTemplate, getContractTemplate, getContractTemplates, previewContractTemplate,
  saveContractTemplateFields, updateContractTemplate, uploadContractTemplate,
} from '../../../api/ContractTemplates/contractTemplates';
import {
  ContractTemplate, ContractTemplateField, ContractVariable, PdfInspection, TemplateKind,
} from '../../../utility/contractTemplates';

const LIST_KEY = [ 'contractTemplates' ];

export function useContractTemplates() {
  const query = useQuery({ queryKey: LIST_KEY, queryFn: () => getContractTemplates() });
  return {
    templates: (query.data?.data.results as ContractTemplate[]) || [],
    variables: (query.data?.data.variables as ContractVariable[]) || [],
    isLoading: query.isLoading,
  };
}

export function useContractTemplate(id: string) {
  const query = useQuery({ queryKey: [ 'contractTemplate', id ], queryFn: () => getContractTemplate(id) });
  return {
    template: (query.data?.data.results as ContractTemplate) || null,
    inspection: (query.data?.data.inspection as PdfInspection) || null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useContractTemplateActions() {
  const queryClient = useQueryClient();
  const refresh = (id?: string) => {
    queryClient.invalidateQueries({ queryKey: LIST_KEY });
    if (id) queryClient.invalidateQueries({ queryKey: [ 'contractTemplate', id ] });
  };

  return {
    upload: useMutation({
      mutationFn: (input: { name: string; kind: TemplateKind; addendum_tag?: string; pdf: File }) => uploadContractTemplate(input),
      onSuccess: () => refresh(),
    }),
    rename: useMutation({
      mutationFn: ({ id, name }: { id: string; name: string }) => updateContractTemplate(id, { name }),
      onSuccess: (_data, vars) => refresh(vars.id),
    }),
    remove: useMutation({ mutationFn: (id: string) => deleteContractTemplate(id), onSuccess: () => refresh() }),
    saveFields: useMutation({
      mutationFn: ({ id, fields }: { id: string; fields: ContractTemplateField[] }) => saveContractTemplateFields(id, fields),
      onSuccess: (_data, vars) => refresh(vars.id),
    }),
    preview: useMutation({ mutationFn: (id: string) => previewContractTemplate(id) }),
  };
}
